import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FiArrowLeft, FiEye } from 'react-icons/fi';
import { fullStoryVideoService } from '../../services/apiService';
import { useT } from '../../utils/i18n';
import { Stepper, isBusy, showError, STEPS } from '../FullStory/shared';
import { FullStoryApiContext } from '../FullStory/api';
import { VoiceStep } from '../FullStory/VoiceStep';
import { VideoStep } from '../FullStory/VideoStep';
import { MaterialStep } from './MaterialStep';
import { ScenesStep } from './ScenesStep';

const POLL_MS = 2500;

/** True while footage is being read (the page keeps polling to show progress). */
export const footageBusy = (story) => (story?.sources || []).some((s) => s.status === 'queued' || s.status === 'processing');

/**
 * One package, step by step — Full Story's editor, with footage. The Voice
 * and Video steps are Full Story's own, talking to this tab's backend.
 */
export const FullStoryVideoEditor = () => {
  const t = useT();
  const { id } = useParams();
  const [story, setStory] = useState(null);
  const [options, setOptions] = useState(null);
  const [step, setStep] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [title, setTitle] = useState('');
  const [uploading, setUploading] = useState({}); // file name → fraction, while uploads run

  const apply = useCallback((next) => {
    setStory(next);
    setTitle((current) => (document.activeElement?.id === 'story-title' ? current : next.title));
    return next;
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([fullStoryVideoService.get(id), fullStoryVideoService.options()])
      .then(([s, o]) => {
        if (cancelled) return;
        apply(s);
        setOptions(o);
        setStep(s.step);
      })
      .catch((e) => {
        if (e.response?.status === 404) setNotFound(true);
        else showError(e);
      });
    return () => { cancelled = true; };
  }, [id, apply]);

  const busy = isBusy(story) || footageBusy(story) || Boolean(story?.images?.progress);
  useEffect(() => {
    if (!busy) return undefined;
    const timer = setInterval(() => {
      fullStoryVideoService.get(id).then(apply).catch(() => {});
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [busy, id, apply]);

  const furthest = story?.step;
  const previousFurthest = useRef(furthest);
  useEffect(() => {
    if (!furthest) return;
    if (previousFurthest.current && STEPS.indexOf(furthest) > STEPS.indexOf(previousFurthest.current)) {
      setStep(furthest);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    previousFurthest.current = furthest;
  }, [furthest]);

  const run = useCallback(async (action) => {
    try {
      return apply(await action());
    } catch (e) {
      showError(e);
      return null;
    }
  }, [apply]);

  const saveTitle = () => {
    const clean = title.trim();
    if (!story?.canEdit || !clean || clean === story.title) { setTitle(story?.title || ''); return; }
    run(() => fullStoryVideoService.update(id, { title: clean }));
  };

  if (notFound) {
    return (
      <div className="card text-center py-12 max-w-lg mx-auto">
        <p className="text-gray-700">{t('fullStory.notFound')}</p>
        <Link to="/full-story-video" className="btn btn-primary mt-4 inline-flex">{t('fullStory.backToStories')}</Link>
      </div>
    );
  }
  if (!story || !options) return <p className="text-sm text-gray-500">{t('fullStory.loading')}</p>;

  const readOnly = !story.canEdit;
  const props = { story, options, run, readOnly, setStory: apply };

  return (
    <FullStoryApiContext.Provider value={fullStoryVideoService}>
      <div className="space-y-4 max-w-6xl">
        <Link to="/full-story-video" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800">
          <FiArrowLeft size={14} /> {t('fullStory.allStories')}
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <input
            id="story-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={saveTitle}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            readOnly={readOnly}
            maxLength={200}
            aria-label={t('fullStory.storyTitle')}
            className="flex-1 min-w-[220px] text-xl sm:text-2xl font-bold text-gray-900 bg-transparent border-0 border-b border-transparent hover:border-gray-200 focus:border-primary-400 focus:ring-0 px-0 py-1"
          />
          {story.costUSD > 0 && (
            <span className="text-xs text-gray-500 tabular-nums" title={t('fullStory.costHint')}>
              {t('fullStory.cost', { cost: story.costUSD.toFixed(2) })}
            </span>
          )}
        </div>
        {readOnly && (
          <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-700">
            <FiEye size={14} className="text-gray-500" />
            {t('fullStory.readOnly', { name: story.owner?.name || '' })}
          </div>
        )}
        <Stepper story={story} current={step} onSelect={setStep} labelPrefix="fullStoryVideo" />
        {step === 'script' && <MaterialStep {...props} uploading={uploading} setUploading={setUploading} />}
        {step === 'voice' && <VoiceStep {...props} />}
        {step === 'images' && <ScenesStep {...props} />}
        {step === 'video' && <VideoStep {...props} />}
      </div>
    </FullStoryApiContext.Provider>
  );
};

export default FullStoryVideoEditor;
